import { useIsDesktop } from "common/MediaQueries"
import { StyledModelImage, StyledModelImageContainer } from "common/ModelImage"
import { setLoading } from "components/shopFeatures/color/colorSlice"
import { useCallback, useEffect, useState } from "react"
import ShopLoader from "reusable/ShopLoader"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"

const StyledContainer = styled(StyledModelImageContainer)<{
  "data-a11y"?: string
}>`
  overflow: hidden;
  cursor: grab;
  position: relative;
  &:focus-visible {
    :after {
      // contact from aria-label
      content: "${(p) => p["data-a11y"]}";
      position: absolute;
      top: 0;
      right: 0;
      padding: 5px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
    }
  }
`

type ThreeSixtyWrapperProps = {
  alt?: string
  imageList: string[]
  selectedColorCode: string
  className?: string
  enable?: boolean
  allEagerLoading?: boolean
}
// THIS ELEMENT IS REUSED IN BOTH SHOP AND MODEL PAGE !!!!
const ThreeSixtyWrapper = ({
  imageList: images,
  alt,
  className,
  enable = true,
  allEagerLoading,
  selectedColorCode,
}: ThreeSixtyWrapperProps) => {
  const [loadingCirclr, setLoadingCirclr] = useState(true)
  const [imagesLeftToLoad, setImagesLeftToLoad] = useState(0)

  const { loading } = useTypedSelector((state) => state.color)
  const [colorsFullyLoaded, setColorsFullyLoaded] = useState<string[]>([])
  const [circlrInstance, setCirclrInstance] = useState(null)

  const dispatch = useAppDispatch()

  const imagesContainerRef = useCallback((node) => {
    if (!node) return

    import("circlr").then(({ default: circlr }) => {
      const circlrObj = circlr(node).scroll(true)
      setLoadingCirclr(false)
      setCirclrInstance(circlrObj)
    })
  }, [])

  useEffect(() => {
    setImagesLeftToLoad(images?.length ?? 0)
    if (colorsFullyLoaded.includes(selectedColorCode)) return
    dispatch(setLoading(true))
  }, [images, dispatch])

  useEffect(() => {
    if (!loadingCirclr && imagesLeftToLoad === 0) {
      dispatch(setLoading(false))
      setColorsFullyLoaded((c) => [...c, selectedColorCode])
    }
  }, [loadingCirclr, imagesLeftToLoad, dispatch])

  const isDesktop = useIsDesktop()

  const getImageProps = (image: string, colorCode: string) =>
    isDesktop
      ? { src: `${image}?width=900&colorCode=${colorCode}` }
      : {
          src: `${image}?width=375&colorCode=${colorCode}`,
          sizes: "100vw",
          srcSet: `${image}?width=375&colorCode=${colorCode} 1x,
                   ${image}?width=750&colorCode=${colorCode} 2x,
                   ${image}?width=1125&colorCode=${colorCode} 3x`,
        }
  if (!images || !selectedColorCode) return null

  return (
    <>
      <StyledContainer
        ref={imagesContainerRef}
        style={{ pointerEvents: loading || !enable ? "none" : "auto" }}
        className={className}
        tabIndex={0}
        role="application"
        data-a11y="רכיב קרוסלת 360 מעלות. ניתן לשנות תמונה על ידי שימוש בחצי המקלדת ימינה ושמאלה"
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            circlrInstance?.next()
          } else if (e.key === "ArrowRight") {
            circlrInstance?.prev()
          }
        }}
      >
        {images.map((image, i) => (
          <StyledModelImage
            key={i}
            {...getImageProps(image, selectedColorCode)}
            style={{ visibility: i === 0 || !loading ? "visible" : "hidden" }}
            onLoad={() => {
              setImagesLeftToLoad((l) => l - 1)
            }}
            onError={() => {
              setImagesLeftToLoad((l) => l - 1)
            }}
            alt={`${alt}=${i}`}
            loading={i === 0 || allEagerLoading ? "eager" : "lazy"}
          />
        ))}
      </StyledContainer>
      {loading && <ShopLoader />}
    </>
  )
}

export default ThreeSixtyWrapper
